import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Loader2, Plus, X } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EventForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditing = !!id;
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(isEditing);
  const [heroFile, setHeroFile] = useState(null);
  const [heroPreview, setHeroPreview] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    hero_image_url: '',
    hero_image_alt: '',
    event_date: '',
    body: '',
    gallery_images: [],
    is_active: true,
  });

  const normalizeGalleryItem = (item) => {
    if (typeof item === 'string') return { url: item, alt: '', caption: '' };
    return {
      url: item?.url || '',
      alt: item?.alt ?? '',
      caption: item?.caption ?? '',
    };
  };

  useEffect(() => {
    if (isEditing) {
      axios.get(`${API}/admin/events/${id}`, { withCredentials: true })
        .then((res) => {
          const d = res.data;
          const gallery = Array.isArray(d.gallery_images)
            ? d.gallery_images.map(normalizeGalleryItem)
            : [];
          setFormData({
            title: d.title || '',
            hero_image_url: d.hero_image_url || '',
            hero_image_alt: d.hero_image_alt || '',
            event_date: d.event_date ? d.event_date.slice(0, 10) : '',
            body: d.body || '',
            gallery_images: gallery,
            is_active: d.is_active !== false,
          });
          if (d.hero_image_url) setHeroPreview(d.hero_image_url.startsWith('/') ? `${BACKEND_URL}${d.hero_image_url}` : d.hero_image_url);
        })
        .catch(() => toast.error('Failed to load event'))
        .finally(() => setLoading(false));
    }
  }, [id, isEditing]);

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleHeroChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setHeroFile(file);
      setHeroPreview(URL.createObjectURL(file));
    }
  };

  const addGalleryImage = () => {
    const url = window.prompt('Image URL (or upload via Events list hero first and paste URL):');
    if (url && url.trim()) {
      handleChange('gallery_images', [...formData.gallery_images, { url: url.trim(), alt: '', caption: '' }]);
    }
  };

  const uploadGalleryImage = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await axios.post(`${API}/admin/events/upload-image`, fd, { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } });
      handleChange('gallery_images', [...formData.gallery_images, { url: data.url, alt: '', caption: '' }]);
      toast.success('Image added to gallery');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    }
    e.target.value = '';
  };

  const bulkUploadGallery = async (e) => {
    const fileList = e.target.files;
    if (!fileList?.length) return;
    const files = Array.from(fileList).filter((f) => /\.(png|jpe?g|gif|webp)$/i.test(f.name));
    if (files.length === 0) {
      toast.error('Please select image files (PNG, JPG, GIF, WebP)');
      e.target.value = '';
      return;
    }
    try {
      const fd = new FormData();
      files.forEach((f) => fd.append('files', f));
      const { data } = await axios.post(`${API}/admin/events/upload-gallery`, fd, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const urls = data.urls || [];
      const newItems = urls.map((url) => ({ url, alt: '', caption: '' }));
      handleChange('gallery_images', [...formData.gallery_images, ...newItems]);
      toast.success(`${newItems.length} image(s) added. Add captions below.`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Bulk upload failed');
    }
    e.target.value = '';
  };

  const updateGalleryItem = (index, field, value) => {
    const next = formData.gallery_images.map((img, i) =>
      i === index ? { ...normalizeGalleryItem(img), [field]: value } : img
    );
    handleChange('gallery_images', next);
  };

  const removeGalleryImage = (index) => {
    handleChange('gallery_images', formData.gallery_images.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title.trim()) {
      toast.error('Title is required');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        title: formData.title.trim(),
        hero_image_url: formData.hero_image_url || null,
        hero_image_alt: formData.hero_image_alt || null,
        event_date: formData.event_date || null,
        body: formData.body || '',
        gallery_images: formData.gallery_images,
        is_active: formData.is_active,
      };
      if (formData.event_date) payload.event_date = new Date(formData.event_date).toISOString();

      let eventId;
      if (isEditing) {
        await axios.put(`${API}/admin/events/${id}`, payload, { withCredentials: true });
        eventId = id;
      } else {
        const { data } = await axios.post(`${API}/admin/events`, payload, { withCredentials: true });
        eventId = data.event_id;
      }

      if (heroFile) {
        const fd = new FormData();
        fd.append('file', heroFile);
        fd.append('alt_text', formData.hero_image_alt || formData.title);
        await axios.post(`${API}/admin/events/${eventId}/hero`, fd, { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } });
      }

      toast.success(isEditing ? 'Event updated' : 'Event created');
      navigate('/admin/events');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-[#C8473A]" />
      </div>
    );
  }

  return (
    <div>
      <Button variant="ghost" className="mb-4" onClick={() => navigate('/admin/events')}>
        <ArrowLeft className="w-4 h-4 mr-2" /> Back to Events
      </Button>
      <h1 className="text-2xl font-bold mb-6">{isEditing ? 'Edit Event' : 'New Event'}</h1>

      <form onSubmit={handleSubmit} className="space-y-6 max-w-3xl">
        <div className="space-y-2">
          <Label>Title *</Label>
          <Input value={formData.title} onChange={(e) => handleChange('title', e.target.value)} placeholder="Event title" />
        </div>
        <div className="space-y-2">
          <Label>Event date</Label>
          <Input type="date" value={formData.event_date} onChange={(e) => handleChange('event_date', e.target.value)} />
        </div>
        <div className="space-y-2">
          <Label>Hero image</Label>
          <div className="flex items-start gap-4">
            {heroPreview && (
              <img src={heroPreview} alt="Hero preview" className="w-40 h-28 object-cover rounded-lg border bg-gray-50" />
            )}
            <div className="flex-1">
              <Input type="file" accept="image/*" onChange={handleHeroChange} className="mb-2" />
              <Input placeholder="Alt text" value={formData.hero_image_alt} onChange={(e) => handleChange('hero_image_alt', e.target.value)} />
            </div>
          </div>
        </div>
        <div className="space-y-2">
          <Label>Write-up (HTML)</Label>
          <Textarea
            value={formData.body}
            onChange={(e) => handleChange('body', e.target.value)}
            rows={12}
            placeholder="Rich text write-up in HTML. You can use &lt;p&gt;, &lt;h2&gt;, &lt;ul&gt;, &lt;a&gt;, etc."
            className="font-mono text-sm"
          />
        </div>
        <div className="space-y-2">
          <Label>Gallery images</Label>
          <p className="text-sm text-gray-500">Add photos, then set a caption for each below.</p>
          <div className="flex flex-wrap gap-2 mb-3">
            <Button type="button" variant="outline" size="sm" onClick={addGalleryImage} className="gap-1">
              <Plus className="w-4 h-4" /> Add by URL
            </Button>
            <label>
              <input type="file" accept="image/*" className="hidden" onChange={uploadGalleryImage} />
              <Button type="button" variant="outline" size="sm" asChild>
                <span className="gap-1 cursor-pointer inline-flex items-center">
                  <Plus className="w-4 h-4" /> Upload one
                </span>
              </Button>
            </label>
            <label>
              <input type="file" accept="image/*" multiple className="hidden" onChange={bulkUploadGallery} />
              <Button type="button" variant="outline" size="sm" asChild>
                <span className="gap-1 cursor-pointer inline-flex items-center">
                  <Plus className="w-4 h-4" /> Bulk upload
                </span>
              </Button>
            </label>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {formData.gallery_images.map((img, idx) => {
              const item = normalizeGalleryItem(img);
              const src = item.url?.startsWith('/') ? `${BACKEND_URL}${item.url}` : item.url;
              return (
                <div key={idx} className="rounded-lg border bg-gray-50 overflow-hidden">
                  <div className="relative group aspect-[4/3] bg-gray-100">
                    <img
                      src={src}
                      alt={item.alt || `Gallery ${idx + 1}`}
                      className="w-full h-full object-cover"
                    />
                    <button
                      type="button"
                      onClick={() => removeGalleryImage(idx)}
                      className="absolute top-1 right-1 p-1 bg-red-500 text-white rounded opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                  <div className="p-2 space-y-1.5">
                    <Input
                      placeholder="Caption (shown under photo)"
                      value={item.caption}
                      onChange={(e) => updateGalleryItem(idx, 'caption', e.target.value)}
                      className="text-sm"
                    />
                    <Input
                      placeholder="Alt text (optional)"
                      value={item.alt}
                      onChange={(e) => updateGalleryItem(idx, 'alt', e.target.value)}
                      className="text-sm"
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Switch checked={formData.is_active} onCheckedChange={(v) => handleChange('is_active', v)} />
          <Label>Active</Label>
        </div>
        <div className="flex gap-4">
          <Button type="button" variant="outline" onClick={() => navigate('/admin/events')}>Cancel</Button>
          <Button type="submit" className="bg-[#C8473A] hover:bg-[#B74F40]" disabled={saving}>
            {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            {isEditing ? 'Update' : 'Create'}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default EventForm;
